import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { GridColumnDef } from '../../hooks/useGridColumns';

type PersonalizarColumnasDialogProps<TId extends string> = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  columns: readonly GridColumnDef<TId>[];
  columnOrder: readonly TId[];
  visibleIds: readonly TId[];
  onToggle: (id: TId) => void;
  onMove: (id: TId, direction: -1 | 1) => void;
  onReorder: (nextOrder: TId[]) => void;
  onReset: () => void;
  addableColumns?: readonly { id: TId; label: string }[];
  onAdd?: (id: TId) => void;
};

type SortableColumnaRowProps = {
  id: string;
  label: string;
  checked: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
};

const SortableColumnaRow: React.FC<SortableColumnaRowProps> = ({
  id,
  label,
  checked,
  canMoveUp,
  canMoveDown,
  onToggle,
  onMoveUp,
  onMoveDown,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <Stack
      ref={setNodeRef}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={0.5}
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: 1,
        border: '1px solid #e0e0e0',
        bgcolor: isDragging ? '#e3f2fd' : '#fafafa',
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.85 : 1,
        zIndex: isDragging ? 1 : 0,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
        <IconButton
          size="small"
          {...attributes}
          {...listeners}
          aria-label={`Arrastrar ${label}`}
          sx={{ cursor: 'grab', touchAction: 'none' }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <FormControlLabel
          sx={{ mr: 0, flex: 1, minWidth: 0 }}
          control={<Checkbox checked={checked} onChange={onToggle} size="small" />}
          label={label}
        />
      </Stack>
      <Stack direction="row" spacing={0}>
        <IconButton size="small" disabled={!canMoveUp} onClick={onMoveUp} aria-label={`Subir ${label}`}>
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" disabled={!canMoveDown} onClick={onMoveDown} aria-label={`Bajar ${label}`}>
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  );
};

export function PersonalizarColumnasDialog<TId extends string>({
  anchorEl,
  onClose,
  columns,
  columnOrder,
  visibleIds,
  onToggle,
  onMove,
  onReorder,
  onReset,
  addableColumns = [],
  onAdd,
}: PersonalizarColumnasDialogProps<TId>) {
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const byId = useMemo(() => new Map(columns.map((c) => [c.id, c])), [columns]);
  const reorderableIds = columnOrder.filter((id) => !byId.get(id)?.alwaysVisible);
  const pinned = columns.filter((c) => c.alwaysVisible);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = reorderableIds.indexOf(active.id as TId);
    const newIndex = reorderableIds.indexOf(over.id as TId);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder([...arrayMove(reorderableIds, oldIndex, newIndex), ...pinned.map((c) => c.id)]);
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      disableScrollLock
      slotProps={{
        paper: {
          sx: {
              width: 320,
              maxHeight: 420,
              overflow: 'auto',
              p: 1.5,
              borderRadius: 2,
          },
        },
      }}
    >
      <Typography variant="subtitle2" sx={{ px: 0.5, mb: 0.5 }}>
        Personalizar columnas
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, display: 'block', mb: 1 }}>
        Arrastrá o usá las flechas para reordenar.
      </Typography>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={reorderableIds} strategy={verticalListSortingStrategy}>
          <Stack spacing={0.75}>
            {reorderableIds.map((columnId, index) => {
              const col = byId.get(columnId);
              if (!col) return null;
              return (
                <SortableColumnaRow
                  key={columnId}
                  id={columnId}
                  label={col.label}
                  checked={visibleIds.includes(columnId)}
                  canMoveUp={index > 0}
                  canMoveDown={index < reorderableIds.length - 1}
                  onToggle={() => onToggle(columnId)}
                  onMoveUp={() => onMove(columnId, -1)}
                  onMoveDown={() => onMove(columnId, 1)}
                />
              );
            })}
          </Stack>
        </SortableContext>
      </DndContext>
      {pinned.map((col) => (
        <Stack
          key={col.id}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            mt: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            border: '1px solid #e0e0e0',
            bgcolor: '#f5f5f5',
          }}
        >
          <FormControlLabel
            control={<Checkbox checked disabled size="small" />}
            label={col.label}
          />
        </Stack>
      ))}
      <Divider sx={{ my: 1.25 }} />
      <Button
        size="small"
        startIcon={<AddIcon />}
        disabled={addableColumns.length === 0}
        onClick={(event) => setAddAnchor(event.currentTarget)}
        sx={{ textTransform: 'none', mb: 1 }}
      >
        Agregar columna
      </Button>
      <Menu
        anchorEl={addAnchor}
        open={Boolean(addAnchor)}
        onClose={() => setAddAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {addableColumns.map((column) => (
          <MenuItem
            key={column.id}
            onClick={() => {
              onAdd?.(column.id);
              setAddAnchor(null);
            }}
          >
            {column.label}
          </MenuItem>
        ))}
      </Menu>
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button size="small" onClick={onReset} sx={{ textTransform: 'none' }}>
          Restablecer
        </Button>
        <Button size="small" variant="contained" onClick={onClose} sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
      </Stack>
    </Popover>
  );
}

export function AccionesColumnHeader({
  label,
  onCustomize,
}: {
  label: string;
  onCustomize: (event: React.MouseEvent<HTMLElement>) => void;
}) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
      <Box component="span">{label}</Box>
      <Tooltip title="Personalizar columnas">
        <IconButton
          size="small"
          aria-label="Personalizar columnas"
          onClick={onCustomize}
          sx={{ color: 'text.secondary' }}
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
